/**
 * SMS admin hooks (Issue #196 — Codex audit fix for missing Admin-Web entry).
 *
 * Wraps the typed `@jinzhuan/api-client` calls for the
 * `/api/v1/admin/sms/templates*` and `/api/v1/admin/sms/logs` endpoints so
 * the SMS admin page can list / create / update templates and inspect
 * sanitised send logs without hand-writing fetch.
 *
 * 红线:
 *   - 必须通过 `@jinzhuan/api-client` typed client 调用后端；禁止手写 fetch /
 *     axios / 纯前端假状态。
 *   - 短信日志 / 模板字段都是后端脱敏后的结果：phoneMask 而非完整手机号，
 *     errorMessage 是 service 截断后的短文本，永远不会包含 OTP / provider
 *     原始 payload。前端不得尝试反推完整手机号 / OTP。
 *   - 模板正文 (`bodyTemplate`) 仅作为 admin 自查用途展示；前端不渲染对客户
 *     的真实短信，避免误用。
 */
import { useCallback, useEffect, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type {
  CreateSmsTemplateRequest,
  SmsSendLogDto,
  SmsDefaultSeedResult,
  SmsTemplateDto,
  UpdateSmsTemplateRequest,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

export type SmsScene = SmsTemplateDto['scene'];
export type SmsTemplateStatus = SmsTemplateDto['status'];
export type SmsSendResult = SmsSendLogDto['result'];

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type {
  CreateSmsTemplateRequest,
  SmsSendLogDto,
  SmsDefaultSeedResult,
  SmsTemplateDto,
  UpdateSmsTemplateRequest,
};

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

// ----- Default template seed -----------------------------------------------

export async function seedDefaultTemplates(): Promise<SmsDefaultSeedResult> {
  const { data, error } = await apiClient.POST('/api/v1/admin/sms/default-templates/seed', {});
  if (error) {
    throw new Error(pickErrorMessage(error, '导入默认模板失败'));
  }
  return data as SmsDefaultSeedResult;
}

// ----- Templates -----------------------------------------------------------

export interface UseSmsTemplatesFilters {
  readonly scene?: SmsScene;
  readonly status?: SmsTemplateStatus;
}

export interface UseSmsTemplatesReturn extends AsyncState<readonly SmsTemplateDto[]> {
  refresh: () => Promise<void>;
  createTemplate: (input: CreateSmsTemplateRequest) => Promise<SmsTemplateDto | null>;
  updateTemplate: (id: string, input: UpdateSmsTemplateRequest) => Promise<SmsTemplateDto | null>;
}

export function useSmsTemplates(filters: UseSmsTemplatesFilters = {}): UseSmsTemplatesReturn {
  const [state, setState] = useState<AsyncState<readonly SmsTemplateDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState({ data: null, loading: true, error: null });
    try {
      const { data, error } = await apiClient.GET('/api/v1/admin/sms/templates', {
        params: { query: { ...filters } },
      });
      if (error) {
        setState({ data: null, loading: false, error: pickErrorMessage(error, '加载短信模板失败') });
        return;
      }
      setState({
        data: (data ?? []) as readonly SmsTemplateDto[],
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({ data: null, loading: false, error: pickErrorMessage(err, '加载短信模板失败') });
    }
  }, [filters.scene, filters.status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createTemplate = useCallback(
    async (input: CreateSmsTemplateRequest): Promise<SmsTemplateDto | null> => {
      const { data, error } = await apiClient.POST('/api/v1/admin/sms/templates', {
        body: input,
      });
      if (error) {
        throw new Error(pickErrorMessage(error, '创建短信模板失败'));
      }
      await refresh();
      return (data ?? null) as SmsTemplateDto | null;
    },
    [refresh],
  );

  const updateTemplate = useCallback(
    async (id: string, input: UpdateSmsTemplateRequest): Promise<SmsTemplateDto | null> => {
      const { data, error } = await apiClient.PATCH('/api/v1/admin/sms/templates/{id}', {
        params: { path: { id } },
        body: input,
      });
      if (error) {
        throw new Error(pickErrorMessage(error, '更新短信模板失败'));
      }
      await refresh();
      return (data ?? null) as SmsTemplateDto | null;
    },
    [refresh],
  );

  return { ...state, refresh, createTemplate, updateTemplate };
}

// ----- Send logs -----------------------------------------------------------

export interface UseSmsSendLogsFilters {
  readonly scene?: SmsScene;
  readonly result?: SmsSendResult;
}

export interface UseSmsSendLogsReturn extends AsyncState<readonly SmsSendLogDto[]> {
  refresh: () => Promise<void>;
}

export function useSmsSendLogs(filters: UseSmsSendLogsFilters = {}): UseSmsSendLogsReturn {
  const [state, setState] = useState<AsyncState<readonly SmsSendLogDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState({ data: null, loading: true, error: null });
    try {
      const { data, error } = await apiClient.GET('/api/v1/admin/sms/logs', {
        params: { query: { ...filters } },
      });
      if (error) {
        setState({
          data: null,
          loading: false,
          error: pickErrorMessage(error, '加载短信日志失败'),
        });
        return;
      }
      setState({
        data: (data ?? []) as readonly SmsSendLogDto[],
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({ data: null, loading: false, error: pickErrorMessage(err, '加载短信日志失败') });
    }
  }, [filters.scene, filters.result]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { ...state, refresh };
}
